import { Field, Input, Label } from "@headlessui/react";
import { forwardRef, useEffect, useRef, useState } from "react";
import EyeOffIcon from "../assets/li_eye-off.svg";
import EyeIcon from "../assets/li_eye.svg";
import { useMainContext } from "../context/MainContext.tsx";
import { useScrollToError } from "../hooks/useScrollToError.ts";
import { nonSpectreJourney } from '../utils/backgroundImage.ts';
import { getContainerClasses, getInputClasses } from "../utils/inputStyles";
import FieldValidationMessage from "./FieldValidationMessage.tsx";

type IProps = {
  className?: string;
  updateFieldValue: (value: string) => void;
  triggerFieldChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  type?: string;
  value?: string;
  /**
   * All the components seem to maintain a local state, which means if the parent consumer
   * wants to set a different value after its initialized it can't.
   * TODO figure out if this local state is actually needed, it might just be able to be removed but needs
   * testing. If it can be removed then the useValuePropOnly prop can be removed as well.
   */
  useValuePropOnly?: boolean;
  id?: string;
  name?: string;
  min?: string;
  max?: string;
  validatemessage?: string;
  fullWidth?: boolean;
  readOnly?: boolean;
  required?: boolean;
  noAutoComplete?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  maxLength?: number;
  nested?: boolean;
  classesMergingType?: "override" | "merge";
  disabled?: boolean;
  inputMode?: string;
};

const Text = forwardRef<HTMLInputElement, IProps>((props, ref) => {
  const { journeyId } = useMainContext();
  const [showPassword, setShowPassword] = useState(false);

  // Pega briefly clears validatemessage between re-renders; debounce clearing so the
  // error state doesn't flash off and back on during a triggerFieldChange cycle.
  const [stableValidateMessage, setStableValidateMessage] = useState(props.validatemessage);
  const clearErrorTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (props.validatemessage) {
      clearTimeout(clearErrorTimeoutRef.current);
      setStableValidateMessage(props.validatemessage);
    } else {
      clearErrorTimeoutRef.current = setTimeout(() => setStableValidateMessage(undefined), 100);
    }
    return () => clearTimeout(clearErrorTimeoutRef.current);
  }, [props.validatemessage]);

  const rootRef = useScrollToError(props.validatemessage, [
    props.value,
    props.updateFieldValue,
  ]);
  const [value, setValue] = useState(props.value || "");

  useEffect(() => {
    if (props.useValuePropOnly) return;
    setValue(props.value || "");
  }, [props.value, props.type, props.useValuePropOnly, props.id]);

  let inputType = props.type || "text";
  if (props.type === "password" && showPassword) {
    inputType = "text";
  }

  const classesMergingType = props.classesMergingType || "merge";
  const containerClasses = getContainerClasses({ nested: props.nested });
  const inputClasses =
    classesMergingType === "merge"
      ? getInputClasses(props.fullWidth, props.className)
      : props.className || "";

  return (
    <div ref={rootRef} className={containerClasses}>
      <Field>
        {props.label && <Label className="!text-base">{props.label}</Label>}
        <div className="relative">
          <Input
            ref={ref}
            type={inputType}
            max={props.max}
            min={props.min}
            placeholder={props.placeholder}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            inputMode={props.inputMode as any}
            readOnly={props.readOnly}
            data-error={!!stableValidateMessage}
            data-populated={!!(props.useValuePropOnly ? props.value : value)}
            id={props.id}
            className={inputClasses}
            name={props.name}
            value={props.useValuePropOnly ? props.value : value}
            maxLength={props.maxLength}
            onChange={(e) => {
              const inputValue = e.target.value;
              props.updateFieldValue(inputValue);
              if (!props.useValuePropOnly) {
                setValue(inputValue);
              }
            }}
            onBlur={
              props.onBlur
                ? props.onBlur
                : (e) => {
                    props.triggerFieldChange(e.target.value);
                  }
            }
            required={props.required ?? false}
            autoComplete={props.noAutoComplete ? "off" : "on"}
            onFocus={props.onFocus}
            disabled={props.disabled}
          />
          {props.type === "password" && (
            <img
              src={showPassword ? EyeOffIcon : EyeIcon}
              alt={showPassword ? "Hide password" : "Show password"}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 cursor-pointer filter"
              style={
                nonSpectreJourney(journeyId) ? { filter: "brightness(0)" } : undefined
              }
              onClick={() => setShowPassword(!showPassword)}
              // style={{ filter: 'invert(23%) sepia(89%) saturate(7472%) hue-rotate(356deg) brightness(91%) contrast(97%)' }} => red
            />
          )}
        </div>
      </Field>
      <FieldValidationMessage message={stableValidateMessage} />
    </div>
  );
});

Text.displayName = "Text";

export default Text;
